/* eslint-disable  @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, AsyncThunk, AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { UserType } from '@nextgen-web-framework/core';
import {
  InternalOptionsApi,
  AccountBalanceAccountType,
  EntryType,
  AccountBalanceEntryEntryTypeEnum,
} from '@commerce-payment-webcomponents/sdk';
import { couponSearchApi } from '@commerce-promotion-webcomponents/sdk';
import { cmsApi, bonusApi } from '../requests';
import { BonusData } from '../models';
import { LocaleType, UserTypeProps, AccountDataType } from '../../types/amshopTypes';

type InitialCmsState = {
  isLoading: boolean;
  hasError: boolean;
  error: any;
  meData: any;
  bannerData: any;
  accountData: AccountDataType;
  bonusData?: BonusData;
  notificationData: any;
  header: any;
  footer: any;
  hasHeaderError?: boolean;
  hasFooterError?: boolean;
};

type GenericAsyncThunk = AsyncThunk<
  any,
  unknown,
  {
    dispatch: any;
    state: InitialCmsState;
  }
>;

type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;

const isPendingGenericAction = (action: AnyAction): action is PendingAction => action.type.endsWith('/pending');

const isRejectedAction = (action: AnyAction): action is RejectedAction => action.type.endsWith('/rejected');

export const getMeData = createAsyncThunk<any, LocaleType & UserTypeProps>(
  'amshop/getMeData',
  ({ language, userType }) =>
    cmsApi.fetchContent({
      language,
      params: [{ key: 'query', value: JSON.stringify({ user_account: { account_type: userType } }) }],
      uId: 'me_panel',
    }),
);

export const getBannerData = createAsyncThunk<any, LocaleType>('amshop/getBannerData', ({ language }) =>
  cmsApi.fetchContent({
    language,
    uId: 'page_banner',
  }),
);

export const getNotificationData = createAsyncThunk<any, LocaleType>('amshop/getNotificationData', ({ language }) =>
  cmsApi.fetchContent({
    language,
    uId: 'notification',
  }),
);

export const getAccountData = createAsyncThunk<any, LocaleType & UserTypeProps>(
  'amShop/getAccountData',
  async ({ userType }) => {
    let amPurseAmt = 0;
    let amPointsAmt = 0;
    let voucherAmt = 0;
    let couponAmt = 0;
    let hasError = false;
    const config = ConfigService.getConfigs();

    if (userType != UserType.ANONYMOUS) {
      await couponSearchApi
        .getUserCoupons({ offset: '0', limit: '-1', filter: `status=='ACTIVE',couponType=='SERIALIZED'` })
        .then((result: any) => {
          couponAmt = result.total;
        })
        .catch(() => {
          hasError = true;
        });
      if (config.NEXT_PUBLIC_CREDIT_VOUCHER) {
        await InternalOptionsApi.getTransactionHistory(
          AccountBalanceAccountType.CreditVoucher as AccountBalanceAccountType,
          {
            entryType: EntryType.All as AccountBalanceEntryEntryTypeEnum,
            pageNumber: 1,
          },
        )
          .then(({ transactionDetails }) => {
            voucherAmt = transactionDetails?.totalBalance ? Number(transactionDetails?.totalBalance) : 0;
          })
          .catch(() => {
            hasError = true;
          });
      }
      switch (userType) {
        case UserType.ABO:
          await InternalOptionsApi.getAmpurseBalance()
            .then((result: any) => {
              const userDetails = result?.userDetails?.[0];
              amPurseAmt = userDetails?.availableBalance ?? 0;
            })
            .catch(() => {
              hasError = true;
            });
          break;
        case UserType.MEMBER:
          await InternalOptionsApi.getLoyalityBalance()
            .then((result: any) => {
              amPointsAmt = result.availablePoints;
            })
            .catch(() => {
              hasError = true;
            });
          break;
        default:
      }
    }

    return { amPurseAmt, amPointsAmt, voucherAmt, couponAmt, isLoaded: true, hasError };
  },
);

export const getBonusData = createAsyncThunk<any, any>('amshop/getBonusData', async ({ period, aboNumber }) => {
  const res = await bonusApi.getPVBV({
    period,
    aboNumber,
  });

  return res;
});

export const getHeader = createAsyncThunk<any>('amshop/getHeader', () =>
  cmsApi.fetchContent({
    uId: 'header',
  }),
);

export const getFooter = createAsyncThunk<any, UserTypeProps>('amshop/getFooter', ({ userType }) =>
  cmsApi.fetchContent({
    uId: 'footer',
    params: [{ key: 'query', value: JSON.stringify({ account_type: { account_type: userType } }) }],
  }),
);

const initialState: InitialCmsState = {
  isLoading: false,
  hasError: false,
  error: {},
  meData: {},
  bannerData: {},
  notificationData: {},
  accountData: { amPurseAmt: 0, amPointsAmt: 0, voucherAmt: 0, isLoaded: false, hasError: false },
  header: null,
  footer: null,
  hasFooterError: false,
  hasHeaderError: false,
};

export const amshopSlice = createSlice({
  name: 'amshop',
  initialState,
  reducers: {
    setHeader: (state: InitialCmsState, action) => {
      state.header = action.payload;
      state.hasHeaderError = false;
    },
    setFooter: (state: InitialCmsState, action) => {
      state.footer = action.payload;
      state.hasFooterError = false;
    },
  },
  extraReducers: (builder) => {
    // API Async Actions
    builder.addCase(getMeData.fulfilled, (state: InitialCmsState, action) => {
      state.meData = action.payload;
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addCase(getBannerData.fulfilled, (state: InitialCmsState, action) => {
      state.bannerData = action.payload;
      state.isLoading = false;
      state.hasError = false;
    });

    builder.addCase(getNotificationData.fulfilled, (state: InitialCmsState, action) => {
      state.notificationData = action.payload;
      state.isLoading = false;
      state.hasError = false;
    });

    builder.addCase(getAccountData.fulfilled, (state: InitialCmsState, action) => {
      state.accountData.amPointsAmt = action.payload?.amPointsAmt;
      state.accountData.amPurseAmt = action.payload?.amPurseAmt;
      state.accountData.voucherAmt = action.payload?.voucherAmt;
      state.accountData.couponAmt = action.payload?.couponAmt;
      state.accountData.isLoaded = action.payload?.isLoaded;
      state.accountData.hasError = action.payload?.hasError;
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addCase(getBonusData.fulfilled, (state: InitialCmsState, action) => {
      state.bonusData = action.payload;
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addCase(getHeader.fulfilled, (state: InitialCmsState, action) => {
      state.header = action.payload;
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addCase(getHeader.rejected, (state: InitialCmsState, action) => {
      state.isLoading = false;
      state.hasHeaderError = true;
      state.error = action;
    });
    builder.addCase(getFooter.fulfilled, (state: InitialCmsState, action) => {
      state.footer = action.payload;
      state.isLoading = false;
      state.hasError = false;
    });
    builder.addCase(getFooter.rejected, (state: InitialCmsState, action) => {
      state.isLoading = false;
      state.hasFooterError = true;
      state.error = action;
    });
    /**
     * Common reducers
     */
    builder.addMatcher(isRejectedAction, (state: InitialCmsState, action: PayloadAction<unknown>) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action;
    });
    builder.addMatcher(isPendingGenericAction, (state: InitialCmsState) => {
      state.isLoading = true;
      state.hasError = false;
    });
  },
});

export const { setFooter, setHeader } = amshopSlice.actions;
